import _axios from '../../../utils/axios';
import { USER, CONFIRMATION_FOR_NODE_COUNT } from '../../../utils/settings';

export async function apiCreateNote(note) {
  try {
    const params = {...note};
    const serverResponse = await _axios.post(USER.NOTES_PATH, params);
    const serverNote = serverResponse?.data?.data;
    if (!serverNote) throw new Error("Unexpected server response");
    return serverNote;
  } catch(error) {
    let errorMsg = error.response?.data?.error || error.message || '' + error;
    throw new Error(errorMsg);
  }
}

export async function apiEditNote(note) {
  try {
    const params = {...note};
    const serverResponse = await _axios.put(USER.NOTES_PATH, params);
    const serverNote = serverResponse?.data?.data;
    if (!serverNote) throw new Error("Unexpected server response");
    return serverNote;
  } catch(error) {
    let errorMsg = error.response?.data?.error || error.message || '' + error;
    throw new Error(errorMsg);
  }
}

export async function apiDeleteNote(note) {
  try {
    const params = {id: note.id};
    await _axios.delete(USER.NOTES_PATH, {data: params});
  } catch(error) {
    let errorMsg = error.response?.data?.error || error.message || '' + error;
    throw new Error(errorMsg);
  }
}

export async function apiGetNotes(nodes) {
  try {
    const params = {nodes: nodes.map(node => ({node_type: node.type, node_value: node.value}))};
    const serverResponse = await _axios.post(USER.NOTES_GET_PATH, params)
    if (serverResponse.data?.data === undefined) throw new Error("Unexpected server response");
    return serverResponse.data.data || [];
  } catch(error) {
    let errorMsg = error.response?.data?.error || error.message || '' + error;
    throw new Error(errorMsg);
  }
}

export async function getCaseFromServer(caseName) {
  try {
    const serverResponse = await _axios.get(USER.CASES_PATH, { params: { name: caseName } })
    const serverCase = serverResponse?.data?.data;
    if (!serverCase) throw new Error('Invalid case');
    // console.log("Server case: ", serverCase);
    return {
      id: serverCase.id,
      name: serverCase.name,
      nodes: serverCase.nodes,
      edges: serverCase.edges,
      createdAt: serverCase.created_at,
      updatedAt: serverCase.updated_at,
      other: serverCase.other ? JSON.parse(serverCase.other) : {}
    };
  } catch (error) {
    let errorMsg = error.response?.data?.error || error.message || '' + error;
    throw new Error(errorMsg);
  };
}

export async function saveCase(caseMetadata, nodes, edges, otherInfo) {
  try {
    const putData = {
      id: caseMetadata.id,
      name: caseMetadata.name,
      nodes,
      edges,
      other: JSON.stringify(otherInfo)
    };
    const serverResponse = await _axios.put(USER.CASES_PATH, putData);
    const {data, ok, error} = serverResponse.data;
    if (!ok) throw new Error("Server failed to save case: " + (error || 'unknown error'));
    return data;
  } catch(error) {
    let errorMsg = error.response?.data?.error || error.message || '' + error;
    throw new Error(errorMsg);
  }
}

export async function runFetcher(nodeType, nodeValue, microserviceUrl, fetcher, timeInterval) {
  try {
    const url = microserviceUrl + '/' + fetcher;
    const postData = {
      type: nodeType,
      value: nodeValue,
      graph: 1
    };
    if (timeInterval?.to > 0 && timeInterval?.from > 0) {
      postData.fromDate = timeInterval.from;
      postData.toDate = timeInterval.to;
    }
    const serverResponse = await _axios.post(url, postData);
    let {ok, nodes, edges} = serverResponse.data;
    if (ok !== 1) throw new Error('Backend returned error' + (serverResponse.data?.error ? ': ' + serverResponse.data.error : '.'));
    if (nodes !== undefined && !(nodes instanceof Object)) throw new Error('Invalid backend response from ' + url);
    if (edges !== undefined && !(edges instanceof Object)) throw new Error('Invalid backend response from ' + url);
    if (nodes === undefined) nodes = [];
    if (edges === undefined) edges = [];
    
    let backwardsCompatibleNodes = [];
    let backwardsCompatibleEdges = [];
    if (nodes.constructor === Array) {
      backwardsCompatibleNodes = nodes;
    } else {
      for (let badId in nodes) {
        const node = nodes[badId];
        if (node.id) { node.value = node.id; delete node.id; }
        backwardsCompatibleNodes.push(node);
      }
    }
    if (edges.constructor === Array) {
      backwardsCompatibleEdges = edges;
    } else {
      for (let badId in edges) {
        const edge = edges[badId];
        if (edge.from_id) { edge.from_value = edge.from_id; delete edge.from_id; }
        if (edge.to_id) { edge.to_value = edge.to_id; delete edge.to_id; }
        backwardsCompatibleEdges.push(edge);
      }
    }

    // check required fields (better to catch errors early, easier to track down the issue in the microservice)
    if (backwardsCompatibleNodes.some(
      node => (!node.type || node.value == null)
    )) throw new Error("Invalid response: Found node without type or value fields");
    if (backwardsCompatibleEdges.some(
      edge => (!edge.from_type || edge.from_value == null || !edge.to_type || edge.to_value == null)
    )) throw new Error("Invalid response: Found edge without required field (from_type/from_value/to_type/to_value)");

    if (backwardsCompatibleNodes.length >= CONFIRMATION_FOR_NODE_COUNT && !window.confirm('The backend ' + url + ' responded with ' + backwardsCompatibleNodes.length + ' nodes. Are you sure you want to display them?')) {
      throw new Error("Canceled by user (too many nodes)");
    }

    return {nodes: backwardsCompatibleNodes, edges: backwardsCompatibleEdges};

  } catch (error) {
    let errorMsg = error.response?.data?.error || error.message || '' + error;
    throw new Error(errorMsg);
  };
}

export async function downloadString(fileName, fileContent) {
  if (Blob === undefined) {
    window.alert("Unsupported browser (no support for Blob)");
    return;
  }
  const blob = new Blob([fileContent], { type: 'text/json' });

  if (window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveBlob(blob, fileName);
  } else {
    const link = window.document.createElement('a');
    link.setAttribute("target", "_blank");
    const objectUrl = window.URL.createObjectURL(blob)
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => { window.URL.revokeObjectURL(objectUrl); }, 10000);
  }
}
